// @ts-strict-ignore
import { ActionType, MatrixModelDataType } from 'flow/store/engine/constants';
import type {
  Action,
  ActionDataObjectKey,
  Attribute,
  SchemaInstance,
  ServiceObject,
} from 'flow/store/engine/types';
import type { WorkflowState } from '../../store/redux/state';
import { computeNextName, parseBooleanString } from '../util';
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import { DISPLAY_NAME_OPTION_KEY } from '../actionOptions';
import {
  REGEX,
  ActionTypeToRegex,
  ACTION_TYPES_ALLOWING_AUTO_RESOLVED_NAME_COLLISIONS,
} from './constants';
import { outputActionHasDataRoutes } from './outputAttributes/outputAttributes';
import type { CreateActionPayload } from 'flow/store/engine/create/actions/types';
import { matrixModelActionHasDataRoutes } from './matrixModels';
import { getPublishedDetails } from './publishedAttributes';
import { LIST_SERVICE_KEY_IN_SERVICES } from '../services/constants';


/**
 * Returns the attribute/key name of the data object in an Action
 * ie. for an input Action -> 'input', and so on.
 * @param action to grab the key from
 *
 * @returns name of data attribute in the action
 */
export function getActionDataObjectKeyName(
  action: Action,
): ActionDataObjectKey {
  switch (action.type) {
    case ActionType.SERVICE:
      return 'service';
    case ActionType.TAG:
      return 'tag';
    case ActionType.INPUT:
      return 'input';
    case ActionType.OUTPUT:
      return 'output';
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return 'importedOutputAttribute';
    case ActionType.OUTCOME:
      return 'outcome';
    case ActionType.MATRIX_MODEL:
      return 'matrixModel';
    case ActionType.OUTCOME_REASON:
      return 'outcomeReason';
    case ActionType.IMPORTED_MATRIX_MODEL:
      return 'importedMatrixModel';
    case ActionType.WORKFLOW:
      return 'workflow';
    case ActionType.SNIPPET:
      return 'snippet';
    default:
      /**
       * this should be updated to throw an Error after better support for Preview Actions
       * is added
       */
      return null;
  }
}


export function getActionTypeText(action: Action, longForm: boolean = false): string {
  switch (action.type) {
    case ActionType.SERVICE:
      return 'Service';
    case ActionType.SNIPPET:
      return 'Snippet';
    case ActionType.TAG:
      return 'Tag';
    case ActionType.INPUT:
      return longForm ? 'Input Attribute' : 'Input';
    case ActionType.OUTPUT:
      return longForm ? 'Output Attribute' : 'Output';
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return longForm ? 'Imported Output Attribute' : 'Imported Output';
    case ActionType.OUTCOME:
      return 'Outcome';
    case ActionType.MATRIX_MODEL:
      return 'Matrix Model';
    case ActionType.OUTCOME_REASON:
      return 'Imported Outcome Reason';
    case ActionType.IMPORTED_MATRIX_MODEL:
      return 'Imported Matrix Model';
    case ActionType.WORKFLOW:
      return 'Workflow';
    case ActionType.EXIT:
      return 'Exit';
    default:
      /**
       * this should be updated to throw an Error after better support for Preview Actions
       * is added
       */
      return '';
  }
}


export function getActionName(action: Action): string {
  switch (action.type) {
    case ActionType.SERVICE: {
      // TODO DPI-2375: clean out the old key after we switch to use new service config
      const displayNameOption = action.options[DISPLAY_NAME_OPTION_KEY];
      if (displayNameOption) {
        return displayNameOption.value as string;
      }
      return action.service.name;
    }
    case ActionType.TAG:
      return action.tag.name;
    case ActionType.INPUT:
      return action.input.name;
    case ActionType.OUTPUT:
      return action.output.name;
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return action.importedOutputAttribute.displayName;
    case ActionType.OUTCOME:
      return action.outcome.name;
    case ActionType.MATRIX_MODEL:
      return action.matrixModel.name;
    case ActionType.OUTCOME_REASON:
      return action.outcomeReason.displayName;
    case ActionType.IMPORTED_MATRIX_MODEL:
      return action.importedMatrixModel.displayName;
    case ActionType.WORKFLOW:
      return action.workflow.name;
    case ActionType.EXIT:
      return action.exit.name;
    default:
      /**
       * this should be updated to throw an Error after better support for Preview Actions
       * is added
       */
      return '';
  }
}

/**
 * Returns a new action with the name set to the given name.
 */
export function setActionName(action: Action, name: string): Action {
  const nextAction = structuredClone(action);

  switch (action.type) {
    case ActionType.SERVICE: {
      // TODO DPI-2375: clean out the old key after we switch to use new service config
      const displayNameOption = action.options[DISPLAY_NAME_OPTION_KEY];
      if (displayNameOption) {
        nextAction.options[DISPLAY_NAME_OPTION_KEY].value = name;
      }
      if (action.service.internalName === LIST_SERVICE_KEY_IN_SERVICES) {
        nextAction.service.name = name;
      }
      return nextAction;
    }
    case ActionType.TAG:
      nextAction.tag.name = name;
      return nextAction;
    case ActionType.INPUT:
      nextAction.input.name = name;
      if (nextAction.input.publishedAttributeToken && nextAction.input.publishedDetails) {
        nextAction.input.publishedDetails.attributeName = name;
      }
      return nextAction;
    case ActionType.OUTPUT:
      nextAction.output.name = name;
      if (nextAction.output.publishedAttributeToken && nextAction.output.publishedDetails) {
        nextAction.output.publishedDetails.attributeName = name;
      }
      return nextAction;
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      nextAction.importedOutputAttribute.displayName = name;
      return nextAction;
    case ActionType.OUTCOME:
      nextAction.outcome.name = name;
      return nextAction;
    case ActionType.MATRIX_MODEL:
      nextAction.matrixModel.name = name;
      return nextAction;
    case ActionType.OUTCOME_REASON:
      nextAction.outcomeReason.displayName = name;
      return nextAction;
    case ActionType.IMPORTED_MATRIX_MODEL:
      nextAction.importedMatrixModel.displayName = name;
      return nextAction;
    case ActionType.WORKFLOW:
      nextAction.workflow.name = name;
      return nextAction;
    case ActionType.EXIT:
      nextAction.exit.name = name;
      return nextAction;
    default:
      // setting the name on other action types with this function is not supported
      return nextAction;
  }
}

export function getActionDataType(side: {
  action?: Action | null;
  attribute?: Attribute | null;
}): string | null {
  const { action } = side;
  if (!action) return null;

  switch (action.type) {
    case ActionType.SERVICE: {
      const { attribute } = side;
      if (!attribute) return null;
      return attribute.dataType;
    }
    case ActionType.MATRIX_MODEL:
      return action.matrixModel.matrixDataType === MatrixModelDataType.STRING
        ? action.matrixModel.matrixDataType
        : 'decimal';
    case ActionType.IMPORTED_MATRIX_MODEL:
      return action.importedMatrixModel.dataType;
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return action.importedOutputAttribute.dataType;
    case ActionType.OUTCOME_REASON:
      return action.outcomeReason.dataType;
    case ActionType.TAG:
      return null; // Tags do not have a data type in this context
    default:
      return action[action.type]?.dataType || null;
  }
}

/**
 * Extracts the unique names from an array of actions.
 * @param actions actions from which to grab the names
 * @returns a Set<string> with unique action names
 */
export function getUniqueNamesFromActions(
  actions: WorkflowState['workflowData']['actions'][string][],
): Set<string> {
  return actions
    .reduce(
      (accum, action) => accum.add(getActionName(action)),
      new Set<string>(),
    );
}

/**
 * Groups all action names into a Map with name Sets, keyed by action type.
 */
export function groupUniqueActionNamesByType(actions: WorkflowState['workflowData']['actions']) {
  const existingActionNamesByType = new Map<ActionType, Set<string>>();
  Object
    .values(actions)
    .forEach(action => {
      const { type } = action;
      if (!existingActionNamesByType.has(type)) {
        existingActionNamesByType.set(type, new Set());
      }
      existingActionNamesByType.get(type).add(getActionName(action));
    });
  return existingActionNamesByType;
}

export function actionHasDisplayName(action: Action): boolean {
  if (!isActionOfType(action, ActionType.SERVICE)) return false;
  if (getActionName(action) === action.service.name) return false;
  return true;
}

export function actionHasRules(action: Action): boolean {
  const { rules } = action;

  if (!rules) {
    return false;
  }

  if (typeof rules === 'object' && Object.keys(rules).length === 0) {
    return false;
  }

  return true;
}

export function actionHasDataRoutes(action: Action): boolean {
  if (isActionOfType(action, ActionType.OUTPUT)) {
    return outputActionHasDataRoutes(action);
  }

  if (isActionOfType(action, ActionType.MATRIX_MODEL)) {
    return matrixModelActionHasDataRoutes(action);
  }

  return false;
}


export function isPublicIdForActionType(actionId: string, actionType: ActionType): boolean {
  const typeFromId = extractActionTypeFromId(actionId);
  return typeFromId === actionType;
}


export function extractActionTypeFromId(actionId: string): Action['type'] {
  const regex = /__(\w+)__/;
  const matches = regex.exec(actionId);

  /**
   * todo - update the DB to use single underscore instead double
   * and remove the legacy regex matching.
   */
  const legacyRegex = /_(\w+)_/;
  const legacyMatches = legacyRegex.exec(actionId);

  if (!matches && !legacyMatches) return null;

  const result = matches?.[1] ?? legacyMatches?.[1] ?? null;

  return result as Action['type'];
}


export function isActionOfType(action: Action, actionType: ActionType | ActionType[]): boolean {
  if (!action) return false;
  const { type } = action;
  const arr = Array.isArray(actionType) ? actionType : [actionType];

  return arr.includes(type);
}


/**
 * Checks if an action is an imported value
 * @param action action to test
 * @returns true if value is imported, false otherwise
 */
export function isActionAnImportedValue(action: Action): boolean {
  if (!action) return false;
  return Boolean(action.importedOutputAttribute)
    || Boolean(action.importedMatrixModel)
    || Boolean(action.outcomeReason);
}


export function doesAttributeActionUseJQ(attributeAction: Action): boolean {
  if (isActionOfType(attributeAction, ActionType.INPUT)) {
    return parseBooleanString(attributeAction.input.useJq);
  }

  if (isActionOfType(attributeAction, ActionType.OUTPUT)) {
    return parseBooleanString(attributeAction.output.useJq);
  }

  return false;
}


// todo - it _might_ be better to move this somewhere else. it's fine here for now
export function getNumericPathIndices(path: string): string[] {
  return path ? path.match(/(\d+)/g) : [];
}

export const getParentRulePathIndicies = pathIndices => pathIndices
  .slice(0, pathIndices.length - 1);

export const getAllowedAttributesWithMatchingDataType = ({
  service,
  incomingDataType,
  workflowType,
}: {
  service: ServiceObject;
  incomingDataType: string;
  workflowType: string;
}) => {
  const { attributes, internal_name: internalName } = service;
  const activeAttributes: Record<string, Attribute> = Object.values(attributes)
    .filter(attributeObj => {
      // filter out attribute with different data type than the left attr data type
      let dataTypeMatching = attributeObj.dataType === incomingDataType;
      if (internalName === 'IdentityElementVelocity') {
        const allowedDatatypes = ['integer', 'decimal'];
        if (allowedDatatypes.includes(incomingDataType)
          && allowedDatatypes.includes(attributeObj.dataType)
        ) {
          dataTypeMatching = true;
        }
      }
      return !attributeObj.archivedAt && dataTypeMatching;
    })
    .reduce((acc, attributeObj) => ({
      ...acc, [attributeObj.attribute_id]: attributeObj,
    }), {});
  /*
    Temporarily hiding features for Portfolio Evaluations
    Epic ID: 109173
  */
  const allowedActiveAttributes: Record<string, Attribute> = (
    getAllowedAttributesForPortfolioWorkflow(
      workflowType,
      service.name,
      activeAttributes,
    )
  );

  return allowedActiveAttributes;
};

export function filterActionsByType(
  actions: Record<string, Action>,
  actionType: ActionType | ActionType[],
) {
  const actionList = Object.values(actions ?? {});
  return actionList.filter(action => isActionOfType(action, actionType));
}


export function getNumberChildActions({
  actionPublicId,
  workflowData,
}: {
  actionPublicId: string;
  workflowData: Partial<SchemaInstance>;
}) {
  const actionReferences = new Set();
  Object.values(workflowData.thresholds).forEach(
    threshold => {
      const thresholdAction = workflowData.actions[threshold.actionId];
      if (
        !isActionOfType(thresholdAction, ActionType.PREVIEW)
        && [
          threshold.left.actionId,
          threshold.right.actionId,
        ].includes(actionPublicId)
      ) {
        actionReferences.add(threshold.actionId);
      }
    });

  Object.values(workflowData.dataRoutes).forEach(
    dataRoute => {
      if (actionPublicId === dataRoute.sourceId) {
        actionReferences.add(dataRoute.targetId);
      } else if (actionPublicId === dataRoute.targetId) {
        actionReferences.add(dataRoute.targetId);
      }
    });

  return [...actionReferences].length;
}

// TODO - expand/refactor this as tkts in this epic are completed
export function isActionDataObjectEphemeral(action: Action): boolean {
  switch (action.type) {
    case ActionType.TAG:
      return Boolean(action.tag?.isEphemeral);
    case ActionType.OUTCOME:
      return Boolean(action.outcome?.isEphemeral);
    case ActionType.INPUT:
    case ActionType.OUTPUT: {
      // if action is attached to PA, returns details. if not - returns null
      const publishedDetails = getPublishedDetails(action);
      return Boolean(publishedDetails?.isEphemeral);
    }
    case ActionType.SERVICE:
      // only list services can be ephemeral
      return Boolean(action.service?.isListEphemeral);
    default:
      return false;
  }
}

export function isActionPayloadEphemeral(payload: CreateActionPayload): boolean {
  if (payload.type === ActionType.TAG || payload.type === ActionType.OUTCOME) {
    return payload.args.isEphemeral;
  }
  return false;
}

/**
 * Computes the next name for an action, preventing collisions with actions of the same type.
 * The default naming rule is to add " (N)" to the end of the name, where N is the number of
 * actions of the same type.
 * @example
 * 2 existing action of same type and name -> "Name (2)".
 */
export function computeNextActionName(action: Action, actions: Record<Action['publicId'], Action>): string {
  const currentActionName = getActionName(action);

  if (ACTION_TYPES_ALLOWING_AUTO_RESOLVED_NAME_COLLISIONS.includes(action.type)) {
    return currentActionName;
  }
  const existingActionNamesForActionType = (
    groupUniqueActionNamesByType(actions).get(action.type) ?? new Set()
  );

  return computeNextName(currentActionName, existingActionNamesForActionType);
}


export function indexActionsByPublicId(actions: Action[]): Record<Action['publicId'], Action> {
  return Object.fromEntries(
    actions.map(action => [action.publicId, action]),
  );
}

export function isActionPublicId(arg: unknown): arg is Action['publicId'] {
  return (
    typeof arg === 'string'
      ? REGEX.ACTION_PUBLIC_ID.test(arg)
      : false
  );
}

export function isActionIdOfType(arg: unknown, actionType: ActionType) {
  if (!isActionPublicId(arg)) {
    return false;
  }

  const regexp = ActionTypeToRegex[actionType];
  return regexp.test(arg);
}

export function includesActionOfType(
  args: { actionIds: Action['publicId'][]; type: ActionType },
): boolean;
export function includesActionOfType(
  args: { actions: Action[]; type: ActionType },
): boolean;

export function includesActionOfType(args: {
  actions?: Action[];
  actionIds?: Action['publicId'][];
  type: ActionType;
}): boolean {
  const { actions, actionIds, type } = args;

  if (actions) {
    return actions.some(action => isActionOfType(action, type));
  }

  if (actionIds) {
    const regexp = ActionTypeToRegex[type];
    return actionIds.some(id => regexp.test(id));
  }

  return false;
}

/**
 * Returns true if the supplied argument is an array of action public ids.
 * An empty array is considered a valid action public id array.
 */
export function isActionPublicIdArray(arg: unknown): arg is Action['publicId'][] {
  return (
    Array.isArray(arg)
      ? arg.every(isActionPublicId)
      : false
  );
}
